import React, { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Grid } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import { ICommission } from '@sparling/financial-common'
import { useHistory } from 'react-router-dom'

import { IDataItem, TableComponent } from '../../../components/Table'
import { columns } from './commissionsList'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { CommissionsFilters } from './CommissionsFilters'
import {
  getCommissionsData,
  commissionsLoading,
  approveCommissionsModal,
  rejectCommissionsModal,
  adjustCommissionsModal,
  adjustConfirmCommissionsModal,
} from '../../../store/selectors'
import {
  getCommissionsRequest,
  openAdjustCommissionsModal,
  openAdjustConfirmCommissionsModal,
  openApproveCommissionsModal,
  openRejectCommissionsModal,
} from '../../../store/actions'
import {
  CommissionsAdjustConfirmModal,
  CommissionsAdjustModal,
  CommissionsApproveModal,
  CommissionsRejectModal,
} from './CommissionsModals'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(2),
    '& p': {
      display: 'none',
    },
  },
  topMrg: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(2),
  },
  title: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  search: {
    paddingRight: 0,
  },
  filters: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    minWidth: 400,
  },
}))

export const Commissions: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()

  const { open: isOpenApproveCommissionsModal } = useSelector(approveCommissionsModal)
  const { open: isOpenAdjustCommissionsModal } = useSelector(adjustCommissionsModal)
  const { open: isOpenAdjustConfirmCommissionsModal } = useSelector(adjustConfirmCommissionsModal)
  const { open: isOpenRejectCommissionsModal } = useSelector(rejectCommissionsModal)
  const loading = useSelector(commissionsLoading)
  const { items, meta } = useSelector(getCommissionsData)

  const [checkedCommissions, setCheckedCommissions] = useState<Record<number, number[]>>({})
  const [amountsForId, setAmountsForId] = useState<{ [key: number]: number }>({})

  const disableAllAction = useMemo(
    () => !Object.values(checkedCommissions).some((ids) => ids.length > 0),
    [checkedCommissions],
  )

  const checkAllIdHandle = useCallback(
    (page: number) => {
      setCheckedCommissions((prev) => {
        const allIds = items.map((item) => item.id)
        const isAllChecked = prev[page]?.length === allIds.length

        setAmountsForId((prevAmounts) => {
          if (isAllChecked) {
            const idsToRemove = new Set(allIds)
            return Object.fromEntries(Object.entries(prevAmounts).filter(([id]) => !idsToRemove.has(+id)))
          }
          const newEntries = items.reduce((acc, item) => {
            acc[item.id] = item.amount
            return acc
          }, {} as { [key: number]: number })

          return { ...prevAmounts, ...newEntries }
        })

        return {
          ...prev,
          [page]: isAllChecked ? [] : allIds,
        }
      })
    },
    [items],
  )

  const checkIdHandle = useCallback(
    (event: MouseEvent<HTMLButtonElement>, page: number) => {
      const { id } = event.currentTarget.dataset
      if (!id) return
      const commissionId = +id
      setCheckedCommissions((prev) => {
        const pageIds = prev[page] || []
        const isChecked = pageIds.includes(commissionId)
        setAmountsForId((prevAmounts) => {
          if (isChecked) {
            const { [commissionId]: _, ...rest } = prevAmounts // eslint-disable-line
            return rest
          }
          const commission = items.find((item) => item.id === commissionId)
          if (!commission) return prevAmounts
          return {
            ...prevAmounts,
            [commissionId]: commission.amount,
          }
        })
        return {
          ...prev,
          [page]: isChecked ? pageIds.filter((p) => p !== commissionId) : [...pageIds, commissionId],
        }
      })
    },
    [items],
  )

  const commissionsArray = useMemo(() => Object.values(checkedCommissions).flat(), [checkedCommissions])

  const loadCommissionsData = useCallback(
    (page = 1) => {
      dispatch(getCommissionsRequest({ page }))
    },
    [dispatch],
  )

  useEffect(() => {
    loadCommissionsData()
  }, [loadCommissionsData])

  useEffect(() => {
    history.push('?page=1')
  }, [history])

  const closeApproveCommissionModal = useCallback(() => {
    dispatch(openApproveCommissionsModal({ open: false, ids: [] }))
  }, [dispatch])

  const closeAdjustCommissionModal = useCallback(() => {
    dispatch(openAdjustCommissionsModal({ open: false, ids: {} }))
  }, [dispatch])

  const closeAdjustConfirmCommissionModal = useCallback(() => {
    dispatch(openAdjustConfirmCommissionsModal({ open: false }))
  }, [dispatch])

  const closeRejectCommissionModal = useCallback(() => {
    dispatch(openRejectCommissionsModal({ open: false, ids: [] }))
  }, [dispatch])

  const approveAllModalHandle = useCallback(() => {
    dispatch(openApproveCommissionsModal({ open: true, ids: commissionsArray }))
  }, [dispatch, commissionsArray])

  const rejectAllModalHandle = useCallback(() => {
    dispatch(openRejectCommissionsModal({ open: true, ids: commissionsArray }))
  }, [dispatch, commissionsArray])

  const adjustModalHandle = useCallback(() => {
    dispatch(openAdjustCommissionsModal({ open: true, ids: amountsForId }))
  }, [dispatch, amountsForId])

  return (
    <Grid container spacing={3} className={classes.topMrg}>
      <Grid item xs={12}>
        <Grid container className={classes.topWrap}>
          <Grid container className={classes.filters}>
            <Grid item xs={10} display={'flex'} alignItems={'center'}>
              <CommissionsFilters />
            </Grid>
            <Grid item xs={2} display="flex" justifyContent={'flex-end'}>
              <GreenButton
                width={140}
                ml={8}
                backgroundColor={'#C92A2A'}
                text={'Reject'}
                disabled={disableAllAction}
                onClick={rejectAllModalHandle}
              />
              <GreenButton
                width={140}
                ml={8}
                text={'Approve'}
                disabled={disableAllAction}
                onClick={approveAllModalHandle}
              />
            </Grid>
          </Grid>
        </Grid>

        <TableComponent
          columns={columns}
          data={items as IDataItem<ICommission>[]}
          params={meta}
          onCheck={checkIdHandle}
          onCheckAll={checkAllIdHandle}
          checkboxIds={checkedCommissions}
          loading={loading}
          loadData={loadCommissionsData}
          isShowDoubleActionBtn={true}
          isShowEditIcon={false}
          isShowInfoIcon={false}
        />
      </Grid>
      <CommissionsApproveModal
        isOpen={isOpenApproveCommissionsModal}
        modalClose={closeApproveCommissionModal}
        clearCheckedIds={() => setCheckedCommissions({})}
        handleAdjust={adjustModalHandle}
      />
      <CommissionsRejectModal
        isOpen={isOpenRejectCommissionsModal}
        modalClose={closeRejectCommissionModal}
        clearCheckedIds={() => setCheckedCommissions({})}
      />
      <CommissionsAdjustModal isOpen={isOpenAdjustCommissionsModal} modalClose={closeAdjustCommissionModal} />
      <CommissionsAdjustConfirmModal
        isOpen={isOpenAdjustConfirmCommissionsModal}
        modalClose={closeAdjustConfirmCommissionModal}
        clearAmounts={() => setAmountsForId({})}
        clearCheckedIds={() => setCheckedCommissions({})}
      />
    </Grid>
  )
}
